// Creator settings → Tools: the timestamps comment posted on YouTube videos (auto-comment),
// its intro text and branding, plus auto-correct words used when generating timestamps.
// Mirrors post_auto_comment in functions/generator: intro + "\n\n" + timestamps + "\n\n" + branding.

import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../../firebase';
import placeholder_profile from '../../../assets/images/profile/placeholder-profile.jpg';
import './commentTemplate.css';

// Written for every new account by functions/infra (onUserCreate)
const DEFAULT_INTRO = 'Hey Everyone 👋\nFind the parts that interest you:';
const BRANDING = 'Chat with videos via Bumpups 🌲';

const SAMPLE_TIMESTAMPS = [
  ['01:05', 'Why timestamps are crucial on YouTube'],
  ['04:12', 'Psychological aspects of engaging users with timestamps'],
  ['07:23', 'How timestamps boost video SEO rankings'],
  ['11:37', 'Comparing videos with and without timestamps - a case study'],
  ['16:49', 'Crafting effective timestamps - best practices'],
];

const CommentTemplate = () => {
  const user = auth.currentUser;
  const [isAutoComment, setIsAutoComment] = useState(true);
  const [isPro, setIsPro] = useState(false);
  const [profileImage, setProfileImage] = useState('');
  const [form, setForm] = useState({ fixedText: DEFAULT_INTRO, isBrandingEnabled: true, correctWords: '' });
  const [saved, setSaved] = useState(form);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  // Editable fields load once, so a change elsewhere can't overwrite unsaved edits
  useEffect(() => {
    if (!user) return;
    Promise.all([
      getDoc(doc(db, 'users', user.uid, 'valueSettings', 'timestamps')),
      getDoc(doc(db, 'users', user.uid, 'userSettings', 'profile')),
    ]).then(([timestamps, profile]) => {
      const t = timestamps.data() || {};
      const loaded = {
        fixedText: t.fixedText ?? DEFAULT_INTRO,
        isBrandingEnabled: t.isBrandingEnabled ?? true,
        correctWords: profile.data()?.correctWords || '',
      };
      setForm(loaded);
      setSaved(loaded);
    }).catch(() => {});
  }, [user]);

  // Live: auto-comment switch (the backend turns it off if YouTube rejects a comment), plan, avatar
  useEffect(() => {
    if (!user) return;
    const unsubs = [
      onSnapshot(doc(db, 'users', user.uid, 'autoSettings', 'youtubeToggles'), snap => {
        setIsAutoComment(snap.data()?.isAutoCommentEnabled ?? true);
      }, () => {}),
      onSnapshot(doc(db, 'users', user.uid, 'subscriptionData', 'planDetails'), snap => {
        setIsPro((snap.data()?.currentPlan || '').toLowerCase() === 'pro');
      }, () => {}),
      onSnapshot(doc(db, 'users', user.uid, 'userSettings', 'profile'), snap => {
        setProfileImage(snap.data()?.profileImage || '');
      }, () => {}),
    ];
    return () => unsubs.forEach(u => u());
  }, [user]);

  const toggleAutoComment = async () => {
    const next = !isAutoComment;
    setIsAutoComment(next);
    try {
      await setDoc(doc(db, 'users', user.uid, 'autoSettings', 'youtubeToggles'), { isAutoCommentEnabled: next }, { merge: true });
    } catch (error) {
      console.error('Error updating auto comment setting:', error);
      setIsAutoComment(!next);
    }
  };

  const save = async () => {
    setIsSaving(true);
    try {
      await Promise.all([
        setDoc(doc(db, 'users', user.uid, 'valueSettings', 'timestamps'),
          { fixedText: form.fixedText, isBrandingEnabled: form.isBrandingEnabled }, { merge: true }),
        setDoc(doc(db, 'users', user.uid, 'userSettings', 'profile'),
          { correctWords: form.correctWords }, { merge: true }),
      ]);
      setSaved(form);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (error) {
      console.error('Error saving comment settings:', error);
    }
    setIsSaving(false);
  };

  const isDirty = JSON.stringify(form) !== JSON.stringify(saved);
  const update = patch => setForm(f => ({ ...f, ...patch }));

  return (
    <>
      <div className="setting-header">Tools</div>

      <div className="account-settings-container">
        <div className="settings-row">
          <div className="settings-label">
            Auto-Comment Timestamps
            <div className="settings-subtitle">Post your video's timestamps as a comment on YouTube</div>
          </div>
          <div className="comment-settings-field">
            <button
              type="button"
              className={`set-switch${isAutoComment ? ' is-on' : ''}`}
              role="switch"
              aria-checked={isAutoComment}
              aria-label="Auto-comment timestamps"
              onClick={toggleAutoComment}
            />
            <button
              className="ui-setting-button ui-setting-button--primary"
              onClick={save}
              disabled={!isDirty || isSaving}
            >
              {justSaved ? 'Saved' : 'Save'}
            </button>
          </div>
        </div>

        {/* Only used for the comment, so it dims while auto-comment is off */}
        <div className={`form-container ${isAutoComment ? '' : 'disabled'}`}>
          <div className="settings-row">
            <div className="comment-settings-field-fixtext">
              <textarea
                id="fixedText"
                aria-label="Comment intro"
                value={form.fixedText}
                maxLength={50}
                onChange={e => update({ fixedText: e.target.value })}
                rows="4"
              />
              <div className="char-count">{`${form.fixedText.length}/50`}</div>
            </div>
          </div>

          <div className="settings-row">
            <div className="comment-settings-field">
              <div className="preview-comment">
                <img src={profileImage || placeholder_profile} alt="" className="prevprofile-image" />
                <div className="preview-text">
                  {form.fixedText && (
                    <div className="preview-block">
                      {form.fixedText.split('\n').map((line, i) => <div className="preview-line" key={i}>{line}</div>)}
                    </div>
                  )}
                  <div className="preview-block">
                    {SAMPLE_TIMESTAMPS.map(([time, text]) => (
                      <div key={time}><span className="timestamp-time">{time}</span> - <span className="timestamp-text">{text}</span></div>
                    ))}
                  </div>
                  {form.isBrandingEnabled && <div className="preview-block">{BRANDING}</div>}
                </div>
              </div>
            </div>
          </div>

          <div className="settings-row">
            <div className="settings-label">
              Remove Branding
              <div className="settings-subtitle">
                {isPro ? `Hide "${BRANDING}" from your comments` : `Upgrade to Pro to remove "${BRANDING}"`}
              </div>
            </div>
            <div className="comment-settings-field">
              <input
                id="tubestampBranding"
                type="checkbox"
                className="styled-checkbox"
                aria-label="Remove branding"
                onChange={() => update({ isBrandingEnabled: !form.isBrandingEnabled })}
                checked={!form.isBrandingEnabled}
                disabled={!isPro}
              />
            </div>
          </div>
        </div>

        <div className="settings-row">
          <div className="settings-label">
            Auto-Correct
            <div className="settings-subtitle">Words often misspelled in your videos (comma separated)</div>
          </div>
          <div className="comment-settings-field-auto">
            <input
              id="correctWords"
              type="text"
              value={form.correctWords}
              maxLength={40}
              onChange={e => update({ correctWords: e.target.value })}
              placeholder="word-1, word-2, word-3"
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CommentTemplate;
